import React, { useCallback, useEffect, useState } from 'react';
import '../assets/assetWorkflow.css';
import './screen.css';
import { OUTCOME_CLASS, STATUS_LABEL, fmtUnit, screenAssistantContext, thresholdText, waitForScreen } from './api.js';

const SEV_CLASS = { high: 'qs-outside', medium: 'qs-review', low: 'qs-nodata', info: 'qs-info' };

export function OutcomePill({ outcome, label }) {
  return (
    <span className={`qs-pill ${OUTCOME_CLASS[outcome] || ''}`} data-testid="qs-outcome">
      {label || STATUS_LABEL[outcome] || outcome}
    </span>
  );
}

export function CriteriaTable({ criteria }) {
  return (
    <table className="qs-table" data-testid="qs-criteria">
      <thead>
        <tr>
          <th>Criterion</th>
          <th>Value</th>
          <th>Result</th>
          <th>Threshold</th>
          <th>Detail</th>
        </tr>
      </thead>
      <tbody>
        {(criteria || []).map((c) => (
          <tr key={c.key} data-testid={`qs-crit-${c.key}`}>
            <td>{c.label}</td>
            <td className="mono">{fmtUnit(c.value, c.unit)}</td>
            <td>
              <span className={`qs-pill ${OUTCOME_CLASS[c.status]}`}>{STATUS_LABEL[c.status]}</span>
            </td>
            <td className="aw-small">
              {thresholdText(c)}
              <div className="aw-small qs-muted">{c.thresholdSource}</div>
            </td>
            <td className="aw-small">{c.detail || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ValueBridge({ rows }) {
  return (
    <table className="qs-table qs-bridge" data-testid="qs-bridge">
      <tbody>
        {(rows || []).map((b) => (
          <tr key={b.key} className={b.total ? 'qs-total' : ''}>
            <td>{b.label}</td>
            <td className="mono">{fmtUnit(b.valueMM, '$MM')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function RiskList({ risks }) {
  if (!risks?.length) return <div className="aw-small">No risks derived from the screen.</div>;
  return (
    <ul className="qs-risks" data-testid="qs-risks">
      {risks.map((r) => (
        <li key={r.id}>
          <span className={`qs-pill ${SEV_CLASS[r.severity] || ''}`}>{r.severity}</span>{' '}
          <b>{r.title}</b> <span className="aw-small qs-muted">· {r.category}</span>
          <div className="aw-small">{r.detail}</div>
          <div className="aw-small qs-muted">Evidence: {r.evidence}</div>
        </li>
      ))}
    </ul>
  );
}

/** Value & risks for one deal: outcome, value bridge, criteria and risk register. */
export default function ValueRisks({ dealId, onContext, compact = false }) {
  const [view, setView] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (start = false) => {
      setBusy(true);
      setErr(null);
      try {
        const v = await waitForScreen(dealId, { start, onTick: setView });
        setView(v);
        onContext?.(screenAssistantContext(v));
      } catch (e) {
        setErr(e.message);
      } finally {
        setBusy(false);
      }
    },
    [dealId, onContext],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const r = view?.result;
  return (
    <section className="abs-section qs-panel" data-testid="value-risks">
      <div className="qs-head">
        <h3>Screen result · Value &amp; risks</h3>
        {r && <OutcomePill outcome={r.outcome} label={r.outcomeLabel} />}
        {r?.demoFixture && <span className="qs-pill qs-nodata">demo fixture</span>}
        {view?.stale && <span className="aw-small qs-muted">inputs changed — re-running</span>}
        <button type="button" className="aw-btn" disabled={busy} onClick={() => load(true)} data-testid="qs-rerun">
          {busy ? 'Screening…' : 'Re-run screen'}
        </button>
        <a className="aw-link qs-screen-link" href="#deals" data-testid="qs-back-dashboard">
          ← All deals
        </a>
      </div>
      {err && <div className="abs-aro-banner" role="status">Screen unavailable: {err}</div>}
      {r?.absError && (
        <div className="abs-aro-banner" role="status">ABS solve failed: {r.absError}</div>
      )}
      {!r && !err && <div className="aw-small">Running quick screen (includes a base ABS solve)…</div>}
      {r && (
        <>
          {r.reason && <div className="aw-small">{r.reason}</div>}
          <div className={compact ? 'qs-grid1' : 'qs-grid'}>
            <div>
              <h4>Value bridge</h4>
              <ValueBridge rows={r.valueBridge} />
            </div>
            <div>
              <h4>Risks</h4>
              <RiskList risks={r.risks} />
            </div>
          </div>
          {!compact && r.criteria?.length > 0 && (
            <>
              <h4>Quick screen criteria</h4>
              <CriteriaTable criteria={r.criteria} />
            </>
          )}
          <div className="aw-small qs-muted">
            Screen run {r.runAt} by {r.runBy}. Screening language only — not an investment recommendation.
          </div>
        </>
      )}
    </section>
  );
}
